import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, Loader2Icon, SendIcon, ShieldCheckIcon, XIcon } from 'lucide-react';
import { NetworkingGuest } from '../../data/networking-guests';
import { CONTACT_EMAIL, GOLD, INTRO_SHEET_URL, NetworkingCopy } from '../../data/networking-copy';
const EASE = [0.23, 1, 0.32, 1] as const;
type FormState = {
  name: string;
  email: string;
  org: string;
  role: string;
  link: string;
  message: string;
};
const EMPTY_FORM: FormState = {
  name: '',
  email: '',
  org: '',
  role: '',
  link: '',
  message: ''
};
const FIELD = 'w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3 text-sm text-white placeholder-white/35 outline-none transition-colors focus:border-[#c9a227] focus:bg-white/10';
const LABEL = 'block text-[11px] font-bold uppercase tracking-[0.18em] text-white/55 mb-2';

/**
 * Collects an introduction request and posts it to the Association's
 * spreadsheet. Guest email addresses are never exposed to the requester.
 */
export function IntroRequestDialog({
  guest,
  onClose,
  c
}: {
  guest: NetworkingGuest;
  onClose: () => void;
  c: NetworkingCopy;
}) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const isValid = form.name.trim() !== '' && /^\S+@\S+\.\S+$/.test(form.email.trim()) && form.message.trim() !== '';
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const submit = () => {
    if (!isValid || status !== 'idle') return;
    setStatus('sending');
    const payload = {
      type: 'voices-ny-introduction',
      timestamp: new Date().toISOString(),
      requestedGuestId: guest.id,
      requestedGuestName: guest.name,
      requesterName: form.name.trim(),
      requesterEmail: form.email.trim(),
      requesterOrg: form.org.trim(),
      requesterRole: form.role.trim(),
      requesterLink: form.link.trim(),
      message: form.message.trim()
    };
    if (INTRO_SHEET_URL) {
      // text/plain avoids a CORS preflight; the JSON arrives in
      // e.postData.contents on the Apps Script side.
      fetch(INTRO_SHEET_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'text/plain;charset=utf-8'
        },
        body: JSON.stringify(payload)
      }).catch(err => {
        console.warn('Introduction request submission error:', err);
      });
    } else {
      const subject = `Introduction request — ${guest.name}`;
      const body = [`Requested guest: ${guest.name}`, `From: ${form.name.trim()}`, `Email: ${form.email.trim()}`, form.org.trim() ? `Organization / company: ${form.org.trim()}` : '', form.role.trim() ? `Role / title: ${form.role.trim()}` : '', form.link.trim() ? `LinkedIn / website: ${form.link.trim()}` : '', '', form.message.trim()].filter(Boolean).join('\n');
      window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
    window.setTimeout(() => setStatus('sent'), 600);
  };
  return <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 sm:p-6 overflow-y-auto" role="dialog" aria-modal="true" aria-label={`${c.introTo} ${guest.name}`} data-id="element-2941" data-mp="1pbw49yfp190e">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} className="fixed inset-0 bg-black/70" onClick={onClose} data-id="element-2942" data-mp="1z9k06rp1cmk5" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.96,
      y: 12
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.97,
      y: 8
    }} transition={{
      duration: 0.24,
      ease: EASE
    }} className="relative w-full max-w-lg my-auto rounded-2xl border border-[#c9a227]/30 bg-[#0b1f3f] shadow-2xl" data-id="element-2943" data-mp="16kv4572u77f9">
        <button onClick={onClose} aria-label={c.cancel} className="absolute right-4 top-4 w-9 h-9 rounded-full flex items-center justify-center text-white/60 transition-colors hover:bg-white/10 hover:text-white" data-id="element-2944" data-mp="3mz6p6uy7onaq">
          <XIcon className="w-5 h-5" data-id="element-2945" data-mp="qf5i7l74ffk" />
        </button>

        {status === 'sent' ? <div className="px-8 py-14 text-center" data-id="element-2946" data-mp="3ry1fsj2f7g5e">
            <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-[#c9a227]/15 flex items-center justify-center" data-id="element-2947" data-mp="9sogeykdmath">
              <CheckCircle2Icon className="w-7 h-7" style={{
            color: GOLD
          }} data-id="element-2948" data-mp="4tll45u77iyp" />
            </div>
            <h2 className="font-bebas text-3xl tracking-wider text-white mb-3" data-id="element-2949" data-mp="2bjrrtq23btf3">
              {c.successTitle}
            </h2>
            <p className="text-sm leading-relaxed text-white/70 max-w-sm mx-auto" data-id="element-2950" data-mp="26mi3kdqftnsu">
              {c.successText}{' '}
              <span className="font-semibold text-white" data-id="element-2951" data-mp="m3xtmvc2pmke">{guest.name}</span>.
            </p>
            <button onClick={onClose} className="mt-8 inline-flex items-center justify-center px-7 py-3 rounded-full text-sm font-bold text-[#0b1f3f] transition-transform duration-150 hover:scale-[1.02]" style={{
          backgroundColor: GOLD
        }} data-id="element-2952" data-mp="r1qx3glzs2gz">
              {c.done}
            </button>
          </div> : <div className="px-6 sm:px-8 pt-8 pb-7" data-id="element-2953" data-mp="8c14n05pbss8">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] mb-2" style={{
          color: GOLD
        }} data-id="element-2954" data-mp="89oi09p00u8u">
              {c.introTo}
            </p>
            <h2 className="font-bebas text-3xl sm:text-4xl tracking-wider text-white leading-none mb-1" data-id="element-2955" data-mp="3ul23eg284neb">
              {guest.name}
            </h2>
            <p className="text-sm text-white/55 mb-5" data-id="element-2956" data-mp="1srw3xa2x3lqm">
              {guest.title ?? guest.organization ?? guest.sector}
            </p>
            <p className="text-sm leading-relaxed text-white/70 mb-7" data-id="element-2957" data-mp="31q482jsxa773">
              {c.formIntro}
            </p>

            <div className="space-y-4" data-id="element-2958" data-mp="2wrkpmtdjfdjh">
              <div data-id="element-2959" data-mp="2sncwmk01rbfg">
                <label className={LABEL} htmlFor="intro-name" data-id="element-2960" data-mp="2sowg0jh022lg">
                  {c.yourName} <span style={{
                color: GOLD
              }} data-id="element-2961" data-mp="2npbbzumz341v">*</span>
                </label>
                <input id="intro-name" type="text" value={form.name} onChange={e => setForm({
              ...form,
              name: e.target.value
            })} className={FIELD} autoComplete="name" data-id="element-2962" data-mp="3kf13ir0235qw" />
              </div>
              <div data-id="element-2963" data-mp="36o5tvchqgkm9">
                <label className={LABEL} htmlFor="intro-email" data-id="element-2964" data-mp="36mkzpnzbz3ox">
                  {c.yourEmail} <span style={{
                color: GOLD
              }} data-id="element-2965" data-mp="7y5o5wfpzk8e">*</span>
                </label>
                <input id="intro-email" type="email" value={form.email} onChange={e => setForm({
              ...form,
              email: e.target.value
            })} className={FIELD} autoComplete="email" data-id="element-2966" data-mp="36xdkx2nlstn" />
              </div>
              <div data-id="element-2967" data-mp="kfq862mchxgq">
                <label className={LABEL} htmlFor="intro-org" data-id="element-2968" data-mp="kf6mqbxhkqxy">
                  {c.yourOrg}
                </label>
                <input id="intro-org" type="text" value={form.org} onChange={e => setForm({
              ...form,
              org: e.target.value
            })} className={FIELD} autoComplete="organization" data-id="element-2969" data-mp="ojefck72er1g" />
              </div>
              <div data-id="element-2970" data-mp="138u45un1cmei">
                <label className={LABEL} htmlFor="intro-role" data-id="element-2971" data-mp="185u9u4fz3n53">
                  {c.yourRole}
                </label>
                <input id="intro-role" type="text" value={form.role} onChange={e => setForm({
              ...form,
              role: e.target.value
            })} className={FIELD} autoComplete="organization-title" data-id="element-2972" data-mp="1c7xof1xxv7ul" />
              </div>
              <div data-id="element-2973" data-mp="1xhchmgwz0avv">
                <label className={LABEL} htmlFor="intro-link" data-id="element-2974" data-mp="1xi4ahjz6nuig">
                  {c.yourLink}
                </label>
                <input id="intro-link" type="url" inputMode="url" value={form.link} onChange={e => setForm({
              ...form,
              link: e.target.value
            })} placeholder={c.yourLinkPlaceholder} aria-describedby="intro-link-help" className={FIELD} autoComplete="url" data-id="element-2975" data-mp="2cdai2bdxg1dw" />
                <p id="intro-link-help" className="mt-2 text-xs leading-relaxed text-white/40" data-id="element-2976" data-mp="1opisi63dlruo">
                  {c.yourLinkHelp}
                </p>
              </div>
              <div data-id="element-2977" data-mp="w0btl600f08w">
                <label className={LABEL} htmlFor="intro-message" data-id="element-2978" data-mp="w14uvrg78xjn">
                  {c.message} <span style={{
                color: GOLD
              }} data-id="element-2979" data-mp="37nylzb7yxd3n">*</span>
                </label>
                <textarea id="intro-message" rows={4} value={form.message} onChange={e => setForm({
              ...form,
              message: e.target.value
            })} placeholder={c.messagePlaceholder} className={`${FIELD} resize-none`} data-id="element-2980" data-mp="3cfqevseljuwy" />
              </div>
            </div>

            <div className="mt-6 flex items-start gap-2.5 text-xs leading-relaxed text-white/45" data-id="element-2981" data-mp="3vd9q92oj1pmj">
              <ShieldCheckIcon className="w-4 h-4 flex-shrink-0 mt-px" style={{
            color: GOLD
          }} data-id="element-2982" data-mp="3hdi6elbbxf0m" />
              <span data-id="element-2983" data-mp="3li81i7y5ypkh">{INTRO_SHEET_URL ? c.privacy : c.fallbackNote}</span>
            </div>

            <div className="mt-7 flex flex-col-reverse sm:flex-row sm:justify-end gap-3" data-id="element-2984" data-mp="25y3cxribx5a9">
              <button onClick={onClose} className="px-6 py-3 rounded-full text-sm font-bold text-white/70 border border-white/20 transition-colors hover:bg-white/5 hover:text-white" data-id="element-2985" data-mp="2a2b19nkvkj7y">
                {c.cancel}
              </button>
              <button onClick={submit} disabled={!isValid || status === 'sending'} className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full text-sm font-bold text-[#0b1f3f] transition-transform duration-150 enabled:hover:scale-[1.02] disabled:opacity-40 disabled:cursor-not-allowed" style={{
            backgroundColor: GOLD
          }} data-id="element-2986" data-mp="dphq6qzvs9um">
                {status === 'sending' ? <>
                    <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-2987" data-mp="3uzfsvbyei8kv" />
                    {c.sending}
                  </> : <>
                    <SendIcon className="w-4 h-4" data-id="element-2988" data-mp="1t71bcdzrr64z" />
                    {c.send}
                  </>}
              </button>
            </div>
          </div>}
      </motion.div>
    </div>;
}